import * as React from 'react'
import { Feature, Map as OlMap, Overlay, View } from 'ol'
import { Control, ScaleLine, defaults as defaultControls } from 'ol/control'
import { Extent, isEmpty } from 'ol/extent'
import olBaseLayer from 'ol/layer/Base'
import VectorLayer from 'ol/layer/Vector'
import { createIconElement } from '../utils/util'
import { DatasetLayer, Layer } from '../state'
import { reifyLayer } from '../reify_layer'
import { DBModels } from '../state/db_models'
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from '../modelling/tile_grid'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style } from 'ol/style'
import Polygon, { fromExtent } from 'ol/geom/Polygon'
import { DragBox, Select, Snap } from 'ol/interaction'
import { platformModifierKeyOnly } from 'ol/events/condition'
import { GeoJSON } from "ol/format"
import Point from "ol/geom/Point"
import { createEmpty, extend as extendExtent, isEmpty as isEmptyExtent } from "ol/extent"
import './map_view.css'
import { SnapshotExtentData } from '../editor/project_editor'
import { getPolygonFromLayer } from '../utils/shape'
import {
  olImageLayerFromLocalPreview,
  parseLocalRgbGeotiffPreview,
  type LocalGeotiffLayerState,
} from "./local_geotiff_preview"
import { slotToKey, type MapStackSlot } from "./map_layer_stack"
import {
  categoryGroupKeyFromProbeSamples,
  collectMapProbeSamples,
  probeCellExtentFromSamples,
  renderProbePopupContent,
  shouldShowMapProbePopup,
} from "./map_coordinate_probe"
import { getTableProbeMatches } from "../editor/model_detail"
import type { Data } from "rete/types/core/data"
import BaseLayer from "ol/layer/Base"
import ImageLayer from "ol/layer/Image"
import { installWebglRenderGuard } from "./webgl_render_guard"

function isPaintbrushGeoJsonLayer(layer: BaseLayer | null | undefined): boolean {
  if (layer == null) return false
  try {
    return Boolean(layer.get("isPaintbrushGeoJSONLayer"))
  } catch {
    return false
  }
}

const INTERNAL_VECTOR_LAYER_NAMES = new Set(["projectExtent", "snapshotExtent", "probeCellHighlight"])
const SKIP_FEATURE_PROPERTIES = new Set([
  "geometry",
  "geom",
  "boundedBy",
  "latitude_dd",
  "longitude_dd",
  "latitude_dms",
  "longitude_dms",
  "village",
  "source_file",
  "record_no",
])
const FEATURE_PROPERTY_ORDER = [
  "local_name",
  "scientific_name",
  "species_class",
  "tags",
  "observation_date",
  "iucn_status",
  "cites_status",
  "ecosystem_role",
]

function isInspectableShapeLayer(layer: BaseLayer | null | undefined): boolean {
  if (layer == null) return false
  try {
    if (INTERNAL_VECTOR_LAYER_NAMES.has(layer.get("name"))) return false
    return layer.get("projectLayerType") === "ShapeLayer"
  } catch {
    return false
  }
}

function formatFeaturePropertyLabel(key: string): string {
  return key.replace(/_/g, " ")
}

function formatFeaturePropertyValue(value: unknown): string {
  if (value == null || value === "") return ""
  if (typeof value === "object") return ""
  const text = String(value).trim()
  if (!text) return ""
  const isoDate = text.match(/^(\d{4}-\d{2}-\d{2})T/)
  return isoDate ? isoDate[1] : text
}

function featurePropertiesForPopup(feature: Feature): Array<{ label: string; value: string }> {
  const props = feature.getProperties()
  const keys = Object.keys(props).filter(key => !SKIP_FEATURE_PROPERTIES.has(key) && formatFeaturePropertyValue(props[key]))
  keys.sort((a, b) => {
    const ia = FEATURE_PROPERTY_ORDER.indexOf(a)
    const ib = FEATURE_PROPERTY_ORDER.indexOf(b)
    if (ia === -1 && ib === -1) return a.localeCompare(b)
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
  })
  return keys.map(key => ({
    label: formatFeaturePropertyLabel(key),
    value: formatFeaturePropertyValue(props[key]),
  }))
}

const FEATURE_POPUP_OFFSET: [number, number] = [10, -8]

function renderFeaturePopupContent(container: HTMLElement, feature: Feature): void {
  container.replaceChildren()
  const wrap = document.createElement("div")
  wrap.className = "map-feature-popup"

  const rows = featurePropertiesForPopup(feature)
  if (rows.length === 0) {
    const empty = document.createElement("p")
    empty.className = "mb-0 text-muted"
    empty.textContent = "No attributes for this feature."
    wrap.appendChild(empty)
  } else {
    const list = document.createElement("dl")
    list.className = "map-feature-popup__list mb-0"
    for (const row of rows) {
      const item = document.createElement("div")
      item.className = "map-feature-popup__row"
      const dt = document.createElement("dt")
      dt.className = "map-feature-popup__label"
      dt.textContent = row.label
      const dd = document.createElement("dd")
      dd.className = "map-feature-popup__value"
      dd.textContent = row.value
      dd.title = row.value
      item.appendChild(dt)
      item.appendChild(dd)
      list.appendChild(item)
    }
    wrap.appendChild(list)
  }

  container.appendChild(wrap)
}

/** Lets the feature popup be dragged from any edge without panning the map. */
function attachFeaturePopupDrag(overlay: Overlay, popupEl: HTMLElement): () => void {
  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return
    if (!popupEl.classList.contains("ol-popup-note--feature")) return
    const target = event.target
    if (!(target instanceof Element) || !target.closest(".ol-popup-drag")) return
    event.preventDefault()

    const startX = event.clientX
    const startY = event.clientY
    const [originX, originY] = overlay.getOffset()
    popupEl.classList.add("ol-popup--dragging")
    popupEl.setPointerCapture(event.pointerId)

    const onPointerMove = (moveEvent: PointerEvent) => {
      overlay.setOffset([
        originX + (moveEvent.clientX - startX),
        originY + (moveEvent.clientY - startY),
      ])
    }

    const endDrag = (endEvent: PointerEvent) => {
      if (popupEl.hasPointerCapture(endEvent.pointerId)) {
        popupEl.releasePointerCapture(endEvent.pointerId)
      }
      popupEl.classList.remove("ol-popup--dragging")
      popupEl.removeEventListener("pointermove", onPointerMove)
      popupEl.removeEventListener("pointerup", endDrag)
      popupEl.removeEventListener("pointercancel", endDrag)
    }

    popupEl.addEventListener("pointermove", onPointerMove)
    popupEl.addEventListener("pointerup", endDrag)
    popupEl.addEventListener("pointercancel", endDrag)
  }

  popupEl.addEventListener("pointerdown", onPointerDown)
  return () => {
    popupEl.removeEventListener("pointerdown", onPointerDown)
  }
}

function shapeFeatureAtPixel(map: OlMap, pixel: [number, number]): { feature: Feature; layerId?: number } | null {
  pruneNullLayersFromMap(map)
  try {
    const hit = map.forEachFeatureAtPixel(
      pixel,
      (feature, layer) => {
        const geomType = (feature as Feature).getGeometry()?.getType()
        if (geomType !== "Point" && geomType !== "MultiPoint") return undefined
        const rawId = layer?.get("projectLayerId")
        const layerId = typeof rawId === "number" ? rawId : Number(rawId)
        return {
          feature: feature as Feature,
          layerId: Number.isFinite(layerId) ? layerId : undefined,
        }
      },
      { layerFilter: isInspectableShapeLayer, hitTolerance: 8 }
    ) as { feature: Feature; layerId?: number } | undefined
    return hit ?? null
  } catch {
    return null
  }
}

/** OL can briefly retain null entries in the layer list while layers are removed. */
function pruneNullLayersFromMap(map: OlMap): void {
  const collection = map.getLayers()
  const layers = collection.getArray()
  for (let i = layers.length - 1; i >= 0; i--) {
    if (layers[i] == null) {
      collection.removeAt(i)
    }
  }
}

type PaintbrushNoteHit = { note: string; coordinate: [number, number] }

function paintbrushNoteAtPixel(
  map: OlMap,
  pixel: [number, number],
  coordinate: [number, number]
): PaintbrushNoteHit | null {
  pruneNullLayersFromMap(map)
  try {
    return map.forEachFeatureAtPixel(
      pixel,
      feature => {
        const geomType = feature.getGeometry()?.getType()
        if (geomType !== "Point" && geomType !== "MultiPoint") return undefined
        const note = feature.get("note")
        const geom = feature.getGeometry()
        let hitCoordinate: [number, number] = coordinate
        if (geom && geom.getType() === "Point") {
          hitCoordinate = (geom as Point).getCoordinates() as [number, number]
        }
        return {
          note: typeof note === "string" ? note : "",
          coordinate: hitCoordinate,
        }
      },
      { layerFilter: isPaintbrushGeoJsonLayer }
    ) as PaintbrushNoteHit | null
  } catch {
    return null
  }
}

function hasPaintbrushNoteAtPixel(map: OlMap, pixel: [number, number]): boolean {
  pruneNullLayersFromMap(map)
  try {
    return Boolean(
      map.forEachFeatureAtPixel(
        pixel,
        feature => {
          const geomType = feature.getGeometry()?.getType()
          return geomType === "Point" || geomType === "MultiPoint"
        },
        { layerFilter: isPaintbrushGeoJsonLayer }
      )
    )
  } catch {
    return false
  }
}

function fitToAvailableExtent(map: OlMap, options: { padding?: [number, number, number, number], duration?: number } = {}) {
  const view = map.getView()
  const layers = map.getLayers().getArray()
  const projectExtentLayer = layers.find(layer => layer != null && layer.get('name') == 'projectExtent')
  const snapshotExtentLayer = layers.find(layer => layer != null && layer.get('name') == 'snapshotExtent')
  const projectExtent = projectExtentLayer instanceof VectorLayer ? projectExtentLayer.getSource()?.getExtent() : undefined
  const snapshotExtent = snapshotExtentLayer instanceof VectorLayer ? snapshotExtentLayer.getSource()?.getExtent() : undefined

  const targetExtent = snapshotExtent || projectExtent

  // If we don't have a valid extent yet, don't try to fit – this avoids OL error #25.
  if (!targetExtent || isEmpty(targetExtent)) {
    return
  }

  const padding =
    options.padding ??
    (map.get("lastExtentFitPadding") as [number, number, number, number] | undefined) ??
    [40, 40, 40, 40]
  const duration = options.duration ?? 900

  map.updateSize()
  view?.fit(targetExtent, { padding, duration })
}

/** Stable identity for layer/CQL/shape extents — used to avoid re-fitting on every render. */
function extentShapeFitKey(kind: "snapshot" | "project", shape: SnapshotExtentData | undefined): string {
  if (!shape) return `${kind}:none`
  return `${kind}:${JSON.stringify({
    layer: shape.layer ?? null,
    cql: shape.cql ?? null,
    externalLink: shape.externalLink ?? null,
    shape: shape.shape ?? null,
    value: shape.value ?? null,
  })}`
}

/**
 * Dispatch map:fitToView only the first time a given extent identity loads.
 * Without this, overlay rebuilds (e.g. from view-change → setZoom re-renders with a
 * fresh projectExtentShape object) re-fetch CQL polygons and re-fit forever, fighting
 * the user whenever they try to pan/zoom.
 */
function maybeFitAfterExtentLoad(
  fittedKeys: React.MutableRefObject<Set<string>>,
  key: string,
) {
  if (fittedKeys.current.has(key)) return
  fittedKeys.current.add(key)
  window.dispatchEvent(new Event('map:fitToView'))
}

function getLayerExtent(layer: olBaseLayer) {
  if (layer instanceof VectorLayer) {
    return layer.getSource().getExtent()
  }
  else {
    return layer.getExtent()
  }
}

export function getExtentShape(extentShape: any) : Polygon | undefined {
  if (extentShape) {
    // sometimes it gets through here, the try catch is to avoid the client crashing
    try {
      const geojson = new GeoJSON().readFeature(extentShape)
      return geojson.getGeometry() as Polygon
    } catch (e) {
      return undefined
    }
  }
  return undefined
}

class FitViewControl extends Control {
  constructor() {
    const button = document.createElement("button")
    button.appendChild(createIconElement("search-location"))
    button.title = "Zoom to fit"

    const element = document.createElement("div")
    element.className = "ol-unselectable ol-control ol-fit-view"
    element.style.left = ".5em"
    element.style.top = "4em"
    element.appendChild(button)

    super({ element })

    button.addEventListener('click', this.handleClick.bind(this), false)
  }

  handleClick() {
    const map = this.getMap()
    if (!map) { return }
    fitToAvailableExtent(map, { duration: 900 })
  }
}

export type ModelOutputCache = Record<number, BooleanTileGrid | NumericTileGrid | CategoricalTileGrid>
export type DatasetCache = Record<number, BooleanTileGrid | NumericTileGrid | CategoricalTileGrid>

interface MapViewProps {
  /** Project layers keyed by sidebar id — only used where `mapLayerStack` references a project slot. */
  projectLayersById: Record<number, Layer>
  /** Bottom → draw order → top (matches sidebar stack when reversed for display). */
  mapLayerStack: MapStackSlot[]
  /** Client-only GeoTIFF previews keyed by ephemeral id — never persisted. */
  localGeotiffLayers: Record<string, LocalGeotiffLayerState>
  onLocalGeotiffParsed?: (layers: LocalGeotiffLayerState[]) => void
  dbModels: DBModels

  initialZoom: number
  setZoom: (zoom: number) => void

  initialCenter: [number, number]
  setCenter: (center: [number, number]) => void

  modelOutputCache: ModelOutputCache
  datasetCache: DatasetCache

  loadTeamDataset: (layer: DatasetLayer) => void

  selectedArea: Extent | null
  setSelectedArea: (area: Extent | null) => void

  selected: boolean
  setSelected: (boolean) => void

  /** When false, shift-drag cannot open Snapshot. Default true. */
  enableSnapshot?: boolean

  projectExtent: Extent
  showProjectExtent: boolean

  extentShape: SnapshotExtentData | undefined  
  setExtentShape: (shape: SnapshotExtentData | undefined) => void

  projectExtentShape: SnapshotExtentData | undefined

  /** When true, use initialZoom/initialCenter and do not fit to project extent (e.g. when zoom/center come from URL) */
  skipFitToExtent?: boolean

  /** When false, omit the metric scale line (distance bar). Default true. */
  showScaleLine?: boolean

  /**
   * Optional [top, right, bottom, left] pixel padding for view.fit(projectExtent).
   * Larger right padding shifts the fitted extent left (e.g. clear of a right sidebar). When omitted, uses [40,40,40,40].
   */
  extentFitPadding?: [number, number, number, number]

  /** Passed to view.fit: do not zoom in closer than this level (see OpenLayers FitOptions.maxZoom). */
  extentFitMaxZoom?: number

  /** If set, run a second fit after this many ms (layout/resize in flex/embed setups). */
  extentFitRetryMs?: number

  /** When set, show team Paintbrush instances (annotations with GeoJSON) as map markers. */
  teamId?: number
  /** Transient UI message shown while Paintbrush layers are refreshed from broadcasts. */
  paintbrushUpdateMessage?: string | null

  /** When the map probe hits a categorical cell, highlight that category on the Detail tab. */
  onProbeCategoryHighlight?: (categoryGroupKey: string | null) => void

  /** Rete model graph — used to look up exposed table values for the probe popup. */
  model?: Data | null

  /**
   * Visibility toggle used by `ProjectEditor` to keep the map alive while the
   * Model View tab is active. When false the root is hidden via `d-none`
   * rather than unmounted, so the OL `Map` instance, its WebGL tile layers,
   * decoded `TileGrid` sources, and any inflight tile requests survive the
   * tab switch — eliminating the visible re-render lag that came from
   * tearing down and rebuilding the whole map on every tab change.
   *
   * Defaults to true to preserve behaviour for callers (e.g.
   * `embed_project_editor`) that don't have tabs.
   */
  visible?: boolean

  /** Name of a map output AEGIS is pointing at. Shown over the map. */
  highlightedLayerName?: string | null
}
export const MapView = ({ projectLayersById, mapLayerStack, localGeotiffLayers, onLocalGeotiffParsed, dbModels, initialZoom, setZoom, initialCenter, setCenter, modelOutputCache, datasetCache, loadTeamDataset, selectedArea, setSelectedArea, selected, setSelected, enableSnapshot = true, projectExtent, showProjectExtent, extentShape, setExtentShape, projectExtentShape, skipFitToExtent, showScaleLine = true, extentFitPadding, extentFitMaxZoom, extentFitRetryMs, teamId, paintbrushUpdateMessage, onProbeCategoryHighlight, model = null, visible = true, highlightedLayerName = null }: MapViewProps) => {
  const mapRef = React.useRef<HTMLDivElement>()
  const popupRef = React.useRef<HTMLDivElement>(null)
  const popupDragLabelRef = React.useRef<HTMLSpanElement>(null)
  const popupContentRef = React.useRef<HTMLDivElement>(null)
  const popupCloserRef = React.useRef<HTMLAnchorElement>(null)
  const probePopupRef = React.useRef<HTMLDivElement>(null)
  const probeContentRef = React.useRef<HTMLDivElement>(null)
  const probeCloserRef = React.useRef<HTMLAnchorElement>(null)
  const projectOlLayerRef = React.useRef(new Map<number, BaseLayer>())
  const localOlLayerRef = React.useRef(new Map<string, ImageLayer<any>>())
  const loadTeamDatasetRef = React.useRef(loadTeamDataset)
  /** Extent identities we've already auto-fitted after async CQL/layer polygon load. */
  const fittedExtentKeysRef = React.useRef<Set<string>>(new Set())
  const lastReportedZoomRef = React.useRef(initialZoom)
  const lastReportedCenterRef = React.useRef<[number, number]>(initialCenter)
  React.useEffect(() => {
    loadTeamDatasetRef.current = loadTeamDataset
  }, [loadTeamDataset])
  const [map, setMap] = React.useState<OlMap | null>(null)
  const [allLayersVisible, setAllLayersVisible] = React.useState(true)
  const [localGeotiffDropMessage, setLocalGeotiffDropMessage] = React.useState<string | null>(null)
  const [localGeotiffLoading, setLocalGeotiffLoading] = React.useState(false)

  const mapStackKey = mapLayerStack.map(slotToKey).join("|")
  const projectLayersDataKey = React.useMemo(
    () =>
      mapLayerStack
        .filter((s): s is Extract<MapStackSlot, { kind: "project" }> => s.kind === "project")
        .map(s => JSON.stringify(projectLayersById[s.layerId]))
        .join("\x1e"),
    [mapLayerStack, projectLayersById],
  )
  const localGeoStateKey = React.useMemo(
    () =>
      mapLayerStack
        .filter((s): s is Extract<MapStackSlot, { kind: "localGeotiff" }> => s.kind === "localGeotiff")
        .map(s => {
          const L = localGeotiffLayers[s.localId]
          return L ? `${s.localId}:${L.opacity}:${L.visible ? 1 : 0}` : ""
        })
        .join("|"),
    [mapLayerStack, localGeotiffLayers],
  )
  const updateAllLayersVisible = (map: OlMap) => {
    const zoom = map.getView().getZoom()
    if (zoom !== undefined) {
      const layerMinZooms = map.getLayers().getArray()
        .filter(layer => layer.getVisible())
        .map(layer => layer.getMinZoom())
      setAllLayersVisible(zoom > Math.max(...layerMinZooms))
    }
  }

  React.useEffect(() => {
    installWebglRenderGuard()
    const newMap = new OlMap({
      view: new View({
        center: initialCenter,
        zoom: initialZoom
      }),
      controls: defaultControls({
        zoomOptions: {
          zoomInLabel: createIconElement("search-plus"),
          zoomOutLabel: createIconElement("search-minus")
        }
      }).extend([
        new FitViewControl(),
        ...(showScaleLine
          ? [new ScaleLine({ units: 'metric', minWidth: 200 })]
          : [])
      ]),
      target: mapRef.current
    });

    newMap.getView().on("change", e => {
      updateAllLayersVisible(newMap)
      const zoom = newMap.getView().getZoom()
      // Skip no-op updates: OL fires many change events during pan/fit animation.
      // Each setState re-render used to rebuild extent overlays (unstable
      // projectExtentShape identity) and re-dispatch fit for CQL extents.
      if (zoom !== undefined && Math.abs(lastReportedZoomRef.current - zoom) > 1e-4) {
        lastReportedZoomRef.current = zoom
        setZoom(zoom)
      }
      const center = newMap.getView().getCenter()
      if (center !== undefined) {
        const next = center as [number, number]
        const prev = lastReportedCenterRef.current
        if (Math.abs(prev[0] - next[0]) > 1e-2 || Math.abs(prev[1] - next[1]) > 1e-2) {
          lastReportedCenterRef.current = next
          setCenter(next)
        }
      }
    })

    setMap(newMap)

    const viewport = newMap.getViewport()
    const onContextLost = (event: Event) => {
      // Tell the browser we will restore; OL recreates the helper on the next frame.
      event.preventDefault()
    }
    viewport.addEventListener("webglcontextlost", onContextLost, true)

    return () => {
      viewport.removeEventListener("webglcontextlost", onContextLost, true)
      newMap.dispose()
      setMap(null)
    }
  }, [])

  React.useEffect(() => {
    if (map === null || !visible) {
      return
    }
    map.updateSize()
    map.render()
  }, [map, visible])

  React.useEffect(() => {
    if (map === null || skipFitToExtent) {
      return
    }
    const pad: [number, number, number, number] = extentFitPadding ?? [40, 40, 40, 40]
    map.set("lastExtentFitPadding", pad)

    const baseFitOpts = {
      padding: pad,
      ...(extentFitMaxZoom !== undefined ? { maxZoom: extentFitMaxZoom } : {}),
    }

    let cancelled = false
    let raf2 = 0
    let retryTimer: number | undefined

    const runFit = (duration: number) => {
      if (cancelled) {
        return
      }
      map.updateSize()
      map.getView().fit(projectExtent, { ...baseFitOpts, duration })
    }

    // Defer fit until after layout: fit() with a 0-size map box locks in a too-close zoom; updateSize() alone later won't refit.
    const id1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => runFit(600))
    })

    if (extentFitRetryMs != null && extentFitRetryMs > 0) {
      retryTimer = window.setTimeout(() => runFit(350), extentFitRetryMs)
    }

    return () => {
      cancelled = true
      cancelAnimationFrame(id1)
      cancelAnimationFrame(raf2)
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer)
      }
    }
  }, [
    map,
    skipFitToExtent,
    projectExtent[0],
    projectExtent[1],
    projectExtent[2],
    projectExtent[3],
    extentFitPadding?.[0],
    extentFitPadding?.[1],
    extentFitPadding?.[2],
    extentFitPadding?.[3],
    extentFitMaxZoom,
    extentFitRetryMs,
  ])

  React.useEffect(() => {
    if (map === null) { return }
    const handler = (_e: Event) => {
      fitToAvailableExtent(map, { duration: 900 })
    }
    window.addEventListener('map:fitToView', handler as EventListener)
    return () => window.removeEventListener('map:fitToView', handler as EventListener)
  }, [map])

  React.useEffect(() => {
    if (map === null) {
      return
    }

    const onPointerMove = (evt: { pixel: [number, number] }) => {
      const notePointHit = hasPaintbrushNoteAtPixel(map, evt.pixel)
      const shapeHit = shapeFeatureAtPixel(map, evt.pixel)
      const el = map.getTargetElement()
      if (el) {
        el.style.cursor = notePointHit || shapeHit ? "pointer" : ""
      }
    }

    ;(map as any).on("pointermove", onPointerMove)

    return () => {
      ;(map as any).un("pointermove", onPointerMove)
      const el = map.getTargetElement()
      if (el) {
        el.style.cursor = ""
      }
    }
  }, [map])

  React.useEffect(() => {
    if (
      map === null ||
      !popupRef.current ||
      !popupDragLabelRef.current ||
      !popupContentRef.current ||
      !popupCloserRef.current ||
      !probePopupRef.current ||
      !probeContentRef.current ||
      !probeCloserRef.current
    ) {
      return
    }

    const noteOverlay = new Overlay({
      element: popupRef.current,
      positioning: "bottom-left",
      offset: FEATURE_POPUP_OFFSET,
      stopEvent: true,
      autoPan: false,
    })
    const detachPopupDrag = attachFeaturePopupDrag(noteOverlay, popupRef.current)
    const probeOverlay = new Overlay({
      element: probePopupRef.current,
      positioning: "bottom-left",
      offset: [10, -8],
      stopEvent: true,
      autoPan: false,
    })
    map.addOverlay(noteOverlay)
    map.addOverlay(probeOverlay)

    /**
     * "You clicked this pixel" outline. Lives on its own vector layer so we
     * can set its z-index above every data/raster layer and the project
     * extent box, but below interactive overlays like the popups themselves
     * (which are HTML, not OL layers, so that's automatic).
     *
     * The layer holds at most one feature — set via
     * `highlightSource.clear() + addFeature(...)` on every click that yields
     * a probe popup, and cleared on note hits / no-popup clicks / popup
     * close.
     */
    const highlightSource = new VectorSource()
    /**
     * Two stacked Style objects give the outline a dark "halo" behind a
     * bright inner stroke — that combination stays legible against any
     * basemap (light terrain, dark satellite, busy categorical raster) where
     * a single-color outline of either shade would blend in. Inner stroke
     * is the same bright yellow as before but thicker; outer stroke is a
     * wider dark band rendered first (lower z-index in the style array =
     * drawn first). Fill is bumped up too for a stronger "this is selected"
     * read at a glance.
     */
    const highlightLayer = new VectorLayer({
      source: highlightSource,
      style: [
        new Style({
          stroke: new Stroke({ color: "rgba(0, 0, 0, 0.85)", width: 9 }),
        }),
        new Style({
          stroke: new Stroke({ color: "#ffe600", width: 5 }),
          fill: new Fill({ color: "rgba(255, 230, 0, 0.32)" }),
        }),
      ],
      zIndex: 1000,
    })
    highlightLayer.set("name", "probeCellHighlight")
    map.addLayer(highlightLayer)

    const clearProbeHighlight = () => {
      highlightSource.clear()
    }
    const showProbeHighlight = (extent: Extent) => {
      highlightSource.clear()
      highlightSource.addFeature(new Feature(fromExtent(extent)))
    }

    popupCloserRef.current.onclick = () => {
      noteOverlay.setOffset(FEATURE_POPUP_OFFSET)
      noteOverlay.setPosition(undefined)
      return false
    }
    probeCloserRef.current.onclick = () => {
      probeOverlay.setPosition(undefined)
      clearProbeHighlight()
      return false
    }

    const onSingleClick = (evt: { pixel: [number, number]; coordinate: [number, number] }) => {
      const noteHit = paintbrushNoteAtPixel(map, evt.pixel, evt.coordinate)

      if (noteHit !== null && noteHit !== undefined) {
        probeOverlay.setPosition(undefined)
        clearProbeHighlight()
        onProbeCategoryHighlight?.(null)
        popupRef.current?.classList.remove("ol-popup-note--feature")
        popupContentRef.current!.textContent = noteHit.note.trim() || "No note"
        noteOverlay.setOffset(FEATURE_POPUP_OFFSET)
        noteOverlay.setPosition(noteHit.coordinate)
        return
      }

      const shapeHit = shapeFeatureAtPixel(map, evt.pixel)
      if (shapeHit) {
        probeOverlay.setPosition(undefined)
        clearProbeHighlight()
        onProbeCategoryHighlight?.(null)
        const layerName = shapeHit.layerId != null ? projectLayersById[shapeHit.layerId]?.name : undefined
        popupRef.current?.classList.add("ol-popup-note--feature")
        popupDragLabelRef.current!.textContent = layerName || "Details"
        renderFeaturePopupContent(popupContentRef.current!, shapeHit.feature)
        noteOverlay.setOffset(FEATURE_POPUP_OFFSET)
        noteOverlay.setPosition(evt.coordinate)
        return
      }

      popupRef.current?.classList.remove("ol-popup-note--feature")
      noteOverlay.setOffset(FEATURE_POPUP_OFFSET)
      noteOverlay.setPosition(undefined)

      const samples = collectMapProbeSamples(
        evt.coordinate,
        mapLayerStack,
        projectLayersById,
        modelOutputCache,
        datasetCache,
        model
      )
      if (!shouldShowMapProbePopup(samples, evt.coordinate, projectExtent)) {
        probeOverlay.setPosition(undefined)
        clearProbeHighlight()
        onProbeCategoryHighlight?.(null)
        return
      }

      renderProbePopupContent(
        probeContentRef.current!,
        samples,
        getTableProbeMatches(model, categoryGroupKeyFromProbeSamples(samples))
      )
      probeOverlay.setPosition(evt.coordinate)
      const cellExtent = probeCellExtentFromSamples(samples)
      if (cellExtent) showProbeHighlight(cellExtent)
      else clearProbeHighlight()
      onProbeCategoryHighlight?.(categoryGroupKeyFromProbeSamples(samples))
    }

    ;(map as any).on("singleclick", onSingleClick)

    return () => {
      ;(map as any).un("singleclick", onSingleClick)
      detachPopupDrag()
      map.removeOverlay(noteOverlay)
      map.removeOverlay(probeOverlay)
      map.removeLayer(highlightLayer)
    }
  }, [map, mapLayerStack, projectLayersById, modelOutputCache, datasetCache, projectExtent, onProbeCategoryHighlight, model])

  React.useEffect(() => {
    if (map === null) {
      return
    }

    const wantedProjectIds = new Set(
      mapLayerStack.filter((s): s is Extract<MapStackSlot, { kind: "project" }> => s.kind === "project").map(s => s.layerId),
    )
    for (const k of Array.from(projectOlLayerRef.current.keys())) {
      if (!wantedProjectIds.has(k)) {
        const stale = projectOlLayerRef.current.get(k)
        if (stale) {
          map.removeLayer(stale)
          stale.dispose()
        }
        projectOlLayerRef.current.delete(k)
      }
    }
    const wantedLocalIds = new Set(
      mapLayerStack.filter((s): s is Extract<MapStackSlot, { kind: "localGeotiff" }> => s.kind === "localGeotiff").map(s => s.localId),
    )
    for (const k of Array.from(localOlLayerRef.current.keys())) {
      if (!wantedLocalIds.has(k)) {
        const stale = localOlLayerRef.current.get(k)
        if (stale) {
          map.removeLayer(stale)
        }
        localOlLayerRef.current.delete(k)
      }
    }

    let stackIndex = 0
    for (const slot of mapLayerStack) {
      if (slot.kind === "project") {
        const layerDef = projectLayersById[slot.layerId]
        if (!layerDef) {
          continue
        }
        const prev = projectOlLayerRef.current.get(slot.layerId) ?? null
        const olLayer = reifyLayer(
          layerDef,
          prev as BaseLayer | null,
          dbModels,
          map,
          modelOutputCache,
          datasetCache,
          layer => loadTeamDatasetRef.current(layer),
          model,
        )
        if (prev && prev !== olLayer) {
          if (map.getLayers().getArray().includes(prev)) {
            map.removeLayer(prev)
          }
          prev.dispose()
        }
        projectOlLayerRef.current.set(slot.layerId, olLayer)
        if (olLayer.getVisible() !== layerDef.visible) {
          olLayer.setVisible(layerDef.visible)
        }
        if (olLayer.getOpacity() !== layerDef.opacity) {
          olLayer.setOpacity(layerDef.opacity)
        }
        const z = stackIndex++
        olLayer.set("mapViewStack", true)
        olLayer.set("mapStackIndex", z)
        olLayer.set("projectLayerId", slot.layerId)
        olLayer.set("projectLayerType", layerDef.type)
        olLayer.setZIndex(z)
        if (!map.getLayers().getArray().includes(olLayer)) {
          map.addLayer(olLayer)
        }
      } else {
        const data = localGeotiffLayers[slot.localId]
        if (!data) {
          continue
        }
        const existing = localOlLayerRef.current.get(slot.localId)
        const olLayer = (existing ?? (olImageLayerFromLocalPreview(data) as ImageLayer<any>))
        if (!existing) {
          localOlLayerRef.current.set(slot.localId, olLayer)
        }
        olLayer.setOpacity(data.opacity)
        olLayer.setVisible(data.visible)
        const z = stackIndex++
        olLayer.set("mapViewStack", true)
        olLayer.set("mapStackIndex", z)
        olLayer.setZIndex(z)
        if (!map.getLayers().getArray().includes(olLayer)) {
          map.addLayer(olLayer)
        }
      }
    }

    // Force a render pass so that newly added/replaced layers (e.g. after a
    // style copy that recreated a WebGLTileLayer) are guaranteed to redraw
    // even when OL's automatic change-event-driven render didn't fire.
    // Skip while the map is `d-none`: hidden canvases often fail
    // `getContext('webgl')` (null), which OL 6 then crashes on createTexture.
    if (visible) {
      map.render()
    }
  }, [
    map,
    mapStackKey,
    projectLayersDataKey,
    localGeoStateKey,
    modelOutputCache,
    datasetCache,
    dbModels,
    model,
    visible,
  ])

  React.useEffect(() => {
    if (map === null) {
      return
    }

    let cancelled = false

    // Remove existing extent overlays before adding updated ones.
    map
      .getLayers()
      .getArray()
      .filter(l => l != null && (l.get("name") === "snapshotExtent" || l.get("name") === "projectExtent"))
      .forEach(l => map.removeLayer(l))

    if (selectedArea && selected) {
      const vectorSource = new VectorSource()
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0)'
          }),
          stroke: new Stroke({
            color: 'slategrey',
            width: 3
          })
        })
      })
      vectorLayer.set('name', 'snapshotExtent')
      // Always draw above project/local data layers (including the hoisted selected layer).
      vectorLayer.setZIndex(1001)

      if (extentShape?.cql && extentShape?.layer) {
        const fitKey = extentShapeFitKey("snapshot", extentShape)
        getPolygonFromLayer(extentShape).then(polygons => {
          if (cancelled) return
          polygons.forEach(polygon => {
            vectorSource.addFeature(new Feature(polygon))
          })
          maybeFitAfterExtentLoad(fittedExtentKeysRef, fitKey)
        })
      } else {
        const polygon = extentShape?.shape ? getExtentShape(extentShape.shape) : fromExtent(selectedArea)
        vectorSource.clear()
        vectorSource.addFeature(new Feature(polygon))
      }

      map.addLayer(vectorLayer)
    }

    const vectorSource = new VectorSource()
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({
          color: showProjectExtent ? 'white' : 'rgba(0, 0, 0, 0)',
          width: 4
        })
      })
    })
    vectorLayer.set('name', 'projectExtent')
    // Always draw above project/local data layers (including the hoisted selected layer).
    vectorLayer.setZIndex(1000)

    if (projectExtentShape?.cql && projectExtentShape?.layer) {
      const fitKey = extentShapeFitKey("project", projectExtentShape)
      getPolygonFromLayer(projectExtentShape).then(polygons => {
        if (cancelled) return
        polygons.forEach(polygon => {
          vectorSource.addFeature(new Feature(polygon))
        })
        maybeFitAfterExtentLoad(fittedExtentKeysRef, fitKey)
      })
    } else {
      const polygon = projectExtentShape?.shape ? getExtentShape(projectExtentShape.shape) : fromExtent(projectExtent)
      vectorSource.clear()
      vectorSource.addFeature(new Feature(polygon))
    }

    map.addLayer(vectorLayer)

    return () => {
      cancelled = true
    }
  }, [
    map,
    selectedArea?.[0],
    selectedArea?.[1],
    selectedArea?.[2],
    selectedArea?.[3],
    selected,
    extentShape?.cql,
    extentShape?.layer,
    extentShape?.shape,
    extentShape?.externalLink,
    extentShape?.value?.[0],
    extentShape?.value?.[1],
    extentShape?.value?.[2],
    extentShape?.value?.[3],
    showProjectExtent,
    projectExtent[0],
    projectExtent[1],
    projectExtent[2],
    projectExtent[3],
    projectExtentShape?.cql,
    projectExtentShape?.layer,
    projectExtentShape?.shape,
    projectExtentShape?.externalLink,
    projectExtentShape?.value?.[0],
    projectExtentShape?.value?.[1],
    projectExtentShape?.value?.[2],
    projectExtentShape?.value?.[3],
  ])

  React.useEffect(() => {
    if (map === null || !enableSnapshot) return

    const dragBox = new DragBox({
      condition: platformModifierKeyOnly
    })

    map.addInteraction(dragBox)

    const onBoxEnd = () => {
      setSelectedArea(dragBox.getGeometry().getExtent())
      setSelected(prev => prev || true)
    }
    const onBoxStart = () => {
      setSelectedArea(null)
      setExtentShape(undefined)
    }
    dragBox.on('boxend', onBoxEnd)
    dragBox.on('boxstart', onBoxStart)

    return () => {
      map.removeInteraction(dragBox)
      dragBox.un('boxend', onBoxEnd)
      dragBox.un('boxstart', onBoxStart)
    }
  }, [map, setSelectedArea, setSelected, setExtentShape, enableSnapshot])

  React.useEffect(() => {
    if (map === null || !(mapRef.current instanceof HTMLElement)) {
      return
    }
    const el = mapRef.current
    const ro = new ResizeObserver(() => map.updateSize())
    ro.observe(el)
    map.updateSize()
    return () => ro.disconnect()
  }, [map])

  /**
   * Force an OL resize/redraw when this view is revealed by the parent tab.
   * While `d-none`, the container is `display: none`, which both zeros the
   * OL viewport size and (per the ResizeObserver spec for boxes with no
   * principal box) suppresses size-change notifications. Without this,
   * coming back to the map tab can leave the canvas blank or sized to a
   * stale 0x0 measurement until something else triggers a resize. We
   * deliberately fire across two RAFs so the browser has committed the
   * flex layout before we ask OL to render.
   */
  React.useEffect(() => {
    if (map === null) {
      return
    }
    if (!visible) {
      return
    }
    let raf2 = 0
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        map.updateSize()
        map.render()
      })
    })
    return () => {
      window.cancelAnimationFrame(raf1)
      if (raf2) window.cancelAnimationFrame(raf2)
    }
  }, [map, visible])

  /** OL draws on nested `.ol-viewport` / canvas; React onDrop on the outer target often never receives the file drop. */
  React.useEffect(() => {
    if (map === null) {
      return
    }
    const el = map.getViewport()
    const opts: AddEventListenerOptions = { capture: true }

    const looksLikeTif = (f: File) =>
      /\.(tif|tiff)$/i.test(f.name) ||
      f.type === 'image/tiff' ||
      f.type === 'image/geotiff' ||
      (f.type === 'application/octet-stream' && /\.(tif|tiff)$/i.test(f.name))

    const filesFromDataTransfer = (dt: DataTransfer): File[] => {
      const fromFiles = dt.files?.length ? Array.from(dt.files) : []
      if (fromFiles.length > 0) {
        return fromFiles
      }
      const out: File[] = []
      if (dt.items) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i]
          if (item.kind === 'file') {
            const f = item.getAsFile()
            if (f) {
              out.push(f)
            }
          }
        }
      }
      return out
    }

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault()
    }
    const onDragOver = (e: DragEvent) => {
      e.preventDefault()
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy'
      }
    }
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!e.dataTransfer) {
        return
      }
      const raw = filesFromDataTransfer(e.dataTransfer)
      if (raw.length === 0) {
        return
      }
      const tifFiles = raw.filter(looksLikeTif)
      if (tifFiles.length === 0) {
        setLocalGeotiffDropMessage('Drop a .tif / .tiff GeoTIFF file to preview it on the map (not saved).')
        window.setTimeout(() => setLocalGeotiffDropMessage(null), 5000)
        return
      }
      setLocalGeotiffDropMessage(null)
      const mapInstance = map
      void (async () => {
        setLocalGeotiffLoading(true)
        try {
          const additions: LocalGeotiffLayerState[] = []
          for (const file of tifFiles) {
            const buf = await file.arrayBuffer()
            const preview = await parseLocalRgbGeotiffPreview(buf)
            const id =
              typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `local-geotiff-${Date.now()}-${Math.random().toString(36).slice(2)}`
            additions.push({
              ...preview,
              id,
              name: file.name,
              visible: true,
              opacity: 0.92,
            })
          }
          onLocalGeotiffParsed?.(additions)
          if (additions.length > 0) {
            let merged = createEmpty()
            for (const a of additions) {
              extendExtent(merged, a.extent3857)
            }
            if (!isEmptyExtent(merged)) {
              window.requestAnimationFrame(() => {
                mapInstance.getView().fit(merged, { duration: 550, padding: [48, 48, 48, 48], maxZoom: 18 })
              })
            }
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          setLocalGeotiffDropMessage(message)
          window.setTimeout(() => setLocalGeotiffDropMessage(null), 8000)
        } finally {
          setLocalGeotiffLoading(false)
        }
      })()
    }

    el.addEventListener('dragenter', onDragEnter, opts)
    el.addEventListener('dragover', onDragOver, opts)
    el.addEventListener('drop', onDrop, opts)
    return () => {
      el.removeEventListener('dragenter', onDragEnter, opts)
      el.removeEventListener('dragover', onDragOver, opts)
      el.removeEventListener('drop', onDrop, opts)
    }
  }, [map, onLocalGeotiffParsed])

  return <div className={`flex-grow-1 position-relative${visible ? "" : " d-none"}`}>
    {highlightedLayerName && (
      <div
        className="bg-warning text-dark rounded-sm px-3 py-2 position-absolute"
        style={{ top: "0.75em", left: "50%", transform: "translateX(-50%)", zIndex: 30, pointerEvents: "none", fontWeight: 600 }}
        role="status"
      >
        {highlightedLayerName}
      </div>
    )}
    {localGeotiffLoading && (
      <div
        className="bg-dark text-light rounded-sm px-3 py-2 position-absolute"
        style={{ top: '0.75em', left: '50%', transform: 'translateX(-50%)', zIndex: 20 }}
      >
        <i className="fas fa-spinner fa-spin mr-2" aria-hidden />
        Loading GeoTIFF…
      </div>
    )}
    {paintbrushUpdateMessage && (
      <div
        className="bg-info text-white rounded-sm px-3 py-2 position-absolute"
        style={{ top: localGeotiffLoading ? '3.5em' : '0.75em', left: '50%', transform: 'translateX(-50%)', zIndex: 20 }}
        role="status"
      >
        <i className="fas fa-sync-alt fa-spin mr-2" aria-hidden />
        {paintbrushUpdateMessage}
      </div>
    )}
    {localGeotiffDropMessage && (
      <div
        className="bg-warning text-dark rounded-sm px-3 py-2 position-absolute"
        style={{ top: localGeotiffLoading ? '3.5em' : '0.75em', left: '50%', transform: 'translateX(-50%)', zIndex: 20, maxWidth: 'min(90%, 28rem)' }}
        role="alert"
      >
        {localGeotiffDropMessage}
        <button
          type="button"
          className="close float-right ml-2"
          aria-label="Dismiss"
          onClick={() => setLocalGeotiffDropMessage(null)}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    )}
    <div className="map-view__pane" style={{ width: '100%', height: '100%' }} ref={mapRef as any} />
    
    <div ref={popupRef} className="ol-popup ol-popup-note">
      <div className="ol-popup-drag ol-popup-drag--top" title="Drag to move">
        <i className="fas fa-grip-vertical" aria-hidden="true" />
        <span ref={popupDragLabelRef} className="ol-popup-drag__label" />
      </div>
      <div className="ol-popup-drag-row">
        <div className="ol-popup-drag ol-popup-drag--side" title="Drag to move" />
        <div ref={popupContentRef} className="ol-popup-body"></div>
        <div className="ol-popup-drag ol-popup-drag--side" title="Drag to move" />
      </div>
      <div className="ol-popup-drag ol-popup-drag--bottom" title="Drag to move">
        <i className="fas fa-grip-lines" aria-hidden="true" />
      </div>
      <a ref={popupCloserRef} href="#" className="ol-popup-closer" aria-label="Close"></a>
    </div>

    <div ref={probePopupRef} className="ol-popup ol-popup-probe">
      <a ref={probeCloserRef} href="#" className="ol-popup-closer"></a>
      <div ref={probeContentRef}></div>
    </div>
    
    {
      !allLayersVisible &&
      <div className="bg-dark text-light rounded-sm px-3 py-2 position-absolute" style={{ bottom: "1em", left: "50%", transform: "translateX(-50%)" }}>
        <i className="fas fa-info-circle" /> Zoom in further to see all layers
      </div>
    }
  </div>
}
